import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { IPublicUser } from '../modules/shared-entities/types/sharedEntities.types';
import api from '../services/api';
import { useFindBrandGeneralSettings } from '../services/modules/brandGeneralSettings/brandGeneralSettings.queries';
import { useFindSharedEntity } from '../services/modules/shared-entities/sharedEntities.queries';
import useGeneralSettingsStore from '../stores/GeneralSettings/useGeneralSettingsStore';
import useSharedEntitystore from '../stores/SharedEntities/sharedEntities.store';
import useSystemParametersStore from '../stores/SystemParameters/useSystemParameters.store';
import { DataQueryKey } from './useDataQuery/dataQuery.types';

export const usePublicApplication = () => {
  const location = useLocation();
  const [isRefreshTokens, setIsRefreshTokens] = useState(false);
  const { updateGeneralSettings } = useGeneralSettingsStore();
  const handleChangeSharedEntity = useSharedEntitystore((state) => state.handleChangeSharedEntity);
  const { id: publicId } = useParams<{ id: string }>();
  const { fetchBrandSystemParameters } = useSystemParametersStore();

  const { data: sharedEntity, isLoading: sharedEntityLoading } = useFindSharedEntity(
    { id: publicId },
    {
      enabled: !!publicId && location.pathname.includes('share'),
    },
  );

  const sharedEntityQueryKey = useMemo(
    () => [
      null,
      DataQueryKey.SHARED_ENTITIES,
      null,

      {
        id: publicId,
      },
    ],
    [],
  );

  const handleRefreshTokens = () => {
    const publicUser = localStorage.getItem('publicUser');

    if (publicUser) {
      const parsedPublicUser: IPublicUser = JSON.parse(publicUser);

      api.setPublicId(parsedPublicUser.publicId!);
      api.setTeam(parsedPublicUser.teamId!);
      api.setBrandContext(parsedPublicUser.brandId!);
      setIsRefreshTokens(true);
    }
  };

  useEffect(() => {
    if (sharedEntity) {
      const publicUser: IPublicUser = {
        brandId: sharedEntity.brand?.id,
        teamId: sharedEntity.teamId,
        publicId: sharedEntity.id,
      };
      localStorage.setItem('publicUser', JSON.stringify(publicUser));
      localStorage.setItem('publicSharedEntity', JSON.stringify(sharedEntity));
      handleChangeSharedEntity(sharedEntity);
      handleRefreshTokens();
    }
  }, [sharedEntity]);

  useFindBrandGeneralSettings({
    options: { enabled: isRefreshTokens && !!sharedEntity?.brand?.id },
    onSuccess: (generalSettings) => updateGeneralSettings(generalSettings),
  });

  useEffect(() => {
    handleRefreshTokens();
  }, []);

  useEffect(() => {
    if (!isRefreshTokens) {
      return;
    }
    fetchBrandSystemParameters();
  }, [isRefreshTokens]);

  return {
    sharedEntityLoading,
    sharedEntity,
    sharedEntityQueryKey,
    handleRefreshTokens,
    isRefreshTokens,
  };
};
